/**
 * Lender Portal - Login Page
 */
import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useToast } from '../../hooks/use-toast';
import {
  Banknote,
  Mail,
  Lock,
  Loader2,
  CheckCircle2,
  LogIn
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LenderLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  
  const justRegistered = location.state?.registered;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await axios.post(`${API_URL}/api/finance/lender/login`, {
        email,
        password
      });
      
      if (response.data.success) {
        // Store lender token
        localStorage.setItem('lender_token', response.data.token);
        localStorage.setItem('lender_info', JSON.stringify(response.data.lender));
        
        toast({
          title: "Login Successful",
          description: `Welcome back, ${response.data.lender.company_name}!`
        });
        
        navigate('/lender/dashboard');
      }
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid credentials';
      setError(message);
      toast({
        title: "Login Failed",
        description: message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="lender-login-form">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4">
            <Banknote className="h-6 w-6 text-white" />
          </div>
          <CardTitle className="text-2xl">Lender Portal</CardTitle>
          <CardDescription>
            Sign in to manage your finance applications
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {justRegistered && (
            <Alert className="mb-4 bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-sm text-green-800">
                Registration submitted! You&apos;ll receive an email once your account is approved.
              </AlertDescription>
            </Alert>
          )}
          
          {error && (
            <Alert className="mb-4 bg-red-50 border-red-200">
              <AlertDescription className="text-sm text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>
        </CardContent>
        
        <CardFooter className="flex flex-col gap-4">
          <Link to="/lender/forgot-password" className="text-sm text-gray-500 hover:text-green-600">
            Forgot your password?
          </Link>
          
          <div className="text-sm text-gray-500">
            Not registered yet?{' '}
            <Link to="/lender/register" className="text-green-600 hover:underline font-medium">
              Apply to join
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default LenderLogin;
